import { useCallback, useEffect, useRef } from 'react'

import type { StepTiming } from './events'

type TimerState = {
  startedAt: number
  /** Focused time already banked from completed stretches. */
  focusedMs: number
  /** When the current focused stretch began, or null while hidden. */
  focusedSince: number | null
}

/**
 * Measures how long the current step has been worked on.
 *
 * BR-40 defines time-on-step as focused time only: a tab left open over lunch
 * is not twenty minutes of struggling with the industry picker, and counting it
 * would put every "stuck" threshold in the wrong place. So the focused
 * accumulator pauses whenever the document is hidden.
 *
 * Wall-clock time is reported alongside it rather than instead of it. The gap
 * between the two is the signal that a step was handed to somebody else — BR-41
 * points out the authentication step is routinely finished days later by whoever
 * owns DNS, and only the two numbers together show that.
 *
 * Returns a getter rather than a value: nothing here belongs in render, and a
 * ticking number would re-render the wizard on a timer for no visible reason.
 */
export const useStepTimer = (step: string): (() => StepTiming) => {
  /**
   * Null until the step's effect has run. `performance.now()` cannot be read
   * during render — it is impure, and the React Compiler rejects it — so the
   * clock starts on commit. Reads before that report zeros, which only happens
   * if something calls the getter before the browser has painted; a zero is
   * honest there, whereas time-since-page-load would be a wrong number.
   */
  const state = useRef<TimerState | null>(null)

  useEffect(() => {
    const now = performance.now()

    state.current = {
      startedAt: now,
      focusedMs: 0,
      focusedSince: document.visibilityState === 'visible' ? now : null
    }
  }, [step])

  useEffect(() => {
    const onVisibilityChange = () => {
      const current = state.current
      if (!current) return

      if (document.visibilityState === 'hidden') {
        if (current.focusedSince !== null) {
          current.focusedMs += performance.now() - current.focusedSince
          current.focusedSince = null
        }

        return
      }

      // Guarded: `visible` can fire without an intervening `hidden` (a
      // `prerender` document becoming active), and restarting the stretch would
      // discard whatever is already banked.
      if (current.focusedSince === null) {
        current.focusedSince = performance.now()
      }
    }

    document.addEventListener('visibilitychange', onVisibilityChange)

    return () =>
      document.removeEventListener('visibilitychange', onVisibilityChange)
  }, [])

  return useCallback((): StepTiming => {
    const current = state.current
    if (!current) return { focused_ms: 0, wall_ms: 0 }

    const now = performance.now()
    const openStretch =
      current.focusedSince === null ? 0 : now - current.focusedSince

    return {
      focused_ms: Math.round(current.focusedMs + openStretch),
      wall_ms: Math.round(now - current.startedAt)
    }
  }, [])
}
